import type { ReactElement } from "react"
import { getFormattedPeriodInfo } from "@core/period-info"
import { getCurrencyCode } from "@core/pricing"
import { PriceFormat } from "@core/types"
import {
  CellType,
  type HorizontalProductBox,
} from "@gatsby-plugin-generic-page/fragments/horizontalProductBox"
import type { PeriodsTableSelect } from "@gatsby-plugin-generic-page/fragments/periodsTableSelect"
import type { TableConfig } from "@gatsby-plugin-generic-page/fragments/tableConfig"
import { SquidexComponentType } from "@gatsby-plugin-generic-page/types/componentType"
import useMenuQuery from "@gatsby-plugin-onestore/hooks/useMenuQuery"
import { typeMatches } from "@onestore-graphql"
import { useLocation } from "@reach/router"
import { get } from "lodash"
import { Helmet } from "react-helmet"
import { helmetJsonLdProp } from "react-schemaorg"
import type { AggregateOffer, Offer, Product, WithContext } from "schema-dts"
import useSiteMetadataQuery from "~/hooks/useSiteMetadataQuery"
import isEmpty from "~/lib/isEmpty"

type SchemaItem = {
  name: string
  price: string
}

function getDefaultPeriodFromConfigs(
  configs: TableConfig[] | undefined
): string | undefined {
  if (isEmpty(configs)) return undefined
  const periodConfig = configs.find((config) =>
    typeMatches(
      config.type.__typename,
      SquidexComponentType.PERIODS_TABLE_SELECT
    )
  )

  if (!periodConfig) return undefined
  return (periodConfig.type as PeriodsTableSelect).defaultPeriodValue?.period
}

function createSingleProductContext(
  item: SchemaItem,
  url: string,
  logoUrl: string,
  brandName: string
): WithContext<Product> {
  const offers: Offer = {
    "@type": "Offer",
    url,
    priceCurrency: getCurrencyCode(),
    price: item.price,
    availability: "https://schema.org/InStock",
    itemCondition: "https://schema.org/NewCondition",
    seller: {
      "@type": "Organization",
      name: brandName,
    },
  }

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: item.name,
    url,
    ...(logoUrl ? { image: logoUrl } : {}),
    brand: {
      "@type": "Brand",
      name: brandName,
    },
    offers,
  }
}

function createAggregateProductContext(
  items: SchemaItem[],
  url: string,
  logoUrl: string,
  brandName: string
): WithContext<Product> {
  const lowPrice = items.reduce((min, item) =>
    parseFloat(item.price) < parseFloat(min.price) ? item : min
  ).price

  const offers: AggregateOffer = {
    "@type": "AggregateOffer",
    url,
    priceCurrency: getCurrencyCode(),
    lowPrice,
    offerCount: items.length,
    availability: "https://schema.org/InStock",
  }

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: items[0].name,
    url,
    ...(logoUrl ? { image: logoUrl } : {}),
    brand: {
      "@type": "Brand",
      name: brandName,
    },
    offers,
  }
}

type Props = {
  products: HorizontalProductBox[]
  configs?: TableConfig[]
}

export default function TableHorizontalBoxesSchema({
  products,
  configs,
}: Props): ReactElement | null {
  const site = useSiteMetadataQuery()
  const { menu } = useMenuQuery()
  const { pathname } = useLocation()
  const pageUrl = `${site.siteUrl}${pathname}`
  const logoUrl: string = get(menu, "flatData.logo[0].url", "")

  const defaultPeriod = getDefaultPeriodFromConfigs(configs)

  const schemaItems = products
    .filter(
      (product) =>
        !isEmpty(product.flatData.saleConfiguration) &&
        !isEmpty(
          get(
            product,
            "flatData.saleConfiguration[0].flatData.defaultCloudBluePlan"
          )
        )
    )
    .map((product) => {
      const plan = get(
        product,
        "flatData.saleConfiguration[0].flatData.defaultCloudBluePlan[0]"
      )
      const nameCell = product.flatData.cells.find(
        (cell) => cell.type === CellType.NAME
      )
      const rawName = nameCell?.value || plan?.flatData?.name || ""
      const name = rawName.replace(/\[\/[^\]]+\]/g, "").trim()
      const price = getFormattedPeriodInfo(
        plan?.flatData?.periods,
        defaultPeriod,
        PriceFormat.REGISTER_NETTO_DOT_SEPARATED
      )

      return { name, price }
    })
    .filter(({ name, price }) => name !== "" && price !== "")

  if (isEmpty(schemaItems)) {
    return null
  }

  if (schemaItems.length === 1) {
    return (
      <Helmet
        script={[
          helmetJsonLdProp(
            createSingleProductContext(
              schemaItems[0],
              pageUrl,
              logoUrl,
              site.ogSiteName
            )
          ),
        ]}
      />
    )
  }

  return (
    <Helmet
      script={[
        helmetJsonLdProp(
          createAggregateProductContext(
            schemaItems,
            pageUrl,
            logoUrl,
            site.ogSiteName
          )
        ),
      ]}
    />
  )
}
